// A lot's internal notes as the device shows them offline (BL-2447 AC 5, ADR 066 — Offline Mode
// § 4.6): a note added, edited or deleted offline changes the cached list at once, in the shape the
// notes route answers, and the queue replays it through the same doors later.
import type { InternalNoteActorDto, InternalNoteDto } from '@auctogon/core-admin/schemas/responses/internal-note';
import { mentionedStaffIds } from '@auctogon/core-admin/internal-note-mentions';

/**
 * BL-2466 — a body's mentions as the route would list them. Offline there is no
 * staff read, so a name is carried over from `known` (the note's mentions before an
 * edit) or left null for the panel to label from its loaded @ list until the sync.
 */
function offlineMentions(body: string, known: readonly InternalNoteActorDto[] = []): InternalNoteActorDto[] {
  const names = new Map(known.map((m) => [m.staffUserId, m.name]));
  return mentionedStaffIds(body).map((staffUserId) => ({ staffUserId, name: names.get(staffUserId) ?? null }));
}

/**
 * A note written offline, as the notes route would answer it: the signed-in user is its author.
 *
 * @param input.id - the note's client id (its queue entry id and `clientOpId`).
 * @param input.lotId - the lot it hangs off.
 * @param input.body - the trimmed text.
 * @param input.author - the signed-in user.
 * @param input.at - now, ISO 8601.
 * @returns the note.
 */
export function offlineNote(input: {
  id: string;
  lotId: string;
  body: string;
  author: InternalNoteActorDto;
  at: string;
}): InternalNoteDto {
  return {
    id: input.id,
    entityType: 'lot',
    entityId: input.lotId,
    body: input.body,
    author: input.author,
    createdAt: input.at,
    updatedAt: input.at,
    isEdited: false,
    viewerCanEdit: true,
    mentions: offlineMentions(input.body),
  };
}

/**
 * The notes typed on a create sheet (plan Q2), shown on the new lot until its create syncs. They
 * ride the create, which the server writes them with, so none can be edited before then.
 *
 * @param input.clientId - the new lot's client id.
 * @param input.bodies - the notes, oldest first, as the create carries them.
 * @param input.author - the signed-in user.
 * @param input.at - now, ISO 8601.
 * @returns the notes, newest first as the notes list reads.
 */
export function createSheetNotes(input: {
  clientId: string;
  bodies: readonly string[];
  author: InternalNoteActorDto;
  at: string;
}): InternalNoteDto[] {
  return input.bodies
    .map((body, i) => ({
      ...offlineNote({ id: `${input.clientId}-note-${i}`, lotId: input.clientId, body, author: input.author, at: input.at }),
      viewerCanEdit: false,
    }))
    .reverse();
}

/**
 * @param input.notes - the lot's cached notes.
 * @param input.noteId - the note edited.
 * @param input.body - its new text.
 * @param input.at - now, ISO 8601.
 * @returns the notes with that one rewritten, and the note as the route answers it; null when absent.
 */
export function withNoteEdited(input: {
  notes: readonly InternalNoteDto[];
  noteId: string;
  body: string;
  at: string;
}): { notes: InternalNoteDto[]; note: InternalNoteDto } | null {
  const current = input.notes.find((n) => n.id === input.noteId);
  if (!current) return null;
  const note: InternalNoteDto = {
    ...current,
    body: input.body,
    updatedAt: input.at,
    isEdited: true,
    // `?? []`: a note cached before BL-2466 has no mentions field.
    mentions: offlineMentions(input.body, current.mentions ?? []),
  };
  return { notes: input.notes.map((n) => (n.id === note.id ? note : n)), note };
}
